import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/core/api";
import type {
  IPackChatContext,
  IPackChatReplyResponse,
  IPackChatThreadDetailResponse,
  IPackChatThreadSummaryResponse,
} from "@/payroll/pack-chat/types";
import {
  invalidateManagedPackThreadQueries,
  updateDetail,
  updateManagedPackThreadListCache,
} from "./cache";
import { managedPackThreadKey, managedPackThreadsKey, path } from "./keys";

export const useStartManagedPackThread = (
  businessId: string | null,
  periodStart: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      technicianId: string;
      runId: string;
      body: string;
      context: IPackChatContext;
    }) => {
      if (!businessId || !periodStart)
        throw new Error("Pay period is required");
      return api
        .post(path(businessId, periodStart), {
          technician_id: payload.technicianId,
          ai_script_run_id: payload.runId,
          body: payload.body,
          context: payload.context,
        })
        .then((res) => res.data as IPackChatThreadDetailResponse);
    },
    onSuccess: async ({ thread }) => {
      await queryClient.cancelQueries({
        queryKey: managedPackThreadsKey(businessId, periodStart),
      });
      queryClient.setQueryData(
        managedPackThreadKey(businessId, periodStart, thread.id),
        { thread },
      );
      return invalidateManagedPackThreadQueries(
        queryClient,
        businessId,
        periodStart,
      );
    },
  });
};

export const useReplyToManagedPackThread = (
  businessId: string | null,
  periodStart: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ threadId, body }: { threadId: string; body: string }) => {
      if (!businessId || !periodStart)
        throw new Error("Pay period is required");
      return api
        .post(`${path(businessId, periodStart)}/${threadId}/messages`, {
          body,
        })
        .then((res) => res.data as IPackChatReplyResponse);
    },
    onSuccess: async ({ thread, message }) => {
      await queryClient.cancelQueries({
        queryKey: managedPackThreadsKey(businessId, periodStart),
      });
      updateManagedPackThreadListCache(
        queryClient,
        businessId,
        periodStart,
        thread,
      );
      updateDetail(
        queryClient,
        businessId,
        periodStart,
        thread.id,
        (current) => ({
          ...current,
          ...thread,
          status: "open",
          messages: current.messages.some((item) => item.id === message.id)
            ? current.messages
            : [...current.messages, message],
        }),
      );
      return invalidateManagedPackThreadQueries(
        queryClient,
        businessId,
        periodStart,
        thread.id,
      );
    },
  });
};

export const useCloseManagedPackThread = (
  businessId: string | null,
  periodStart: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (threadId: string) => {
      if (!businessId || !periodStart)
        throw new Error("Pay period is required");
      return api
        .patch(`${path(businessId, periodStart)}/${threadId}/close`)
        .then((res) => res.data as IPackChatThreadSummaryResponse);
    },
    onSuccess: async ({ thread }) => {
      await queryClient.cancelQueries({
        queryKey: managedPackThreadsKey(businessId, periodStart),
      });
      updateManagedPackThreadListCache(
        queryClient,
        businessId,
        periodStart,
        thread,
      );
      updateDetail(
        queryClient,
        businessId,
        periodStart,
        thread.id,
        (current) => ({ ...current, ...thread, messages: current.messages }),
      );
      return invalidateManagedPackThreadQueries(
        queryClient,
        businessId,
        periodStart,
        thread.id,
      );
    },
  });
};
